// Read-only diagnostics for the browser leg of Telephony Preview.
// Keep the allowlist narrow: RTCStats can include addresses and device IDs.
type Stat = Record<string, unknown>;
type StatsReport = { forEach(callback: (stat: Stat) => void): void };

const mediaFields = [
  'ssrc',
  'packetsSent', 'packetsReceived', 'packetsLost', 'packetsDiscarded',
  'bytesSent', 'bytesReceived', 'jitter', 'roundTripTime', 'fractionLost',
  'jitterBufferDelay', 'jitterBufferEmittedCount', 'concealedSamples',
  'silentConcealedSamples', 'concealmentEvents', 'totalSamplesReceived',
  'insertedSamplesForDeceleration', 'removedSamplesForAcceleration',
  'audioLevel', 'totalAudioEnergy', 'totalSamplesDuration',
] as const;

function pickNumbers<K extends string>(source: Stat, keys: readonly K[]): Partial<Record<K, number>> {
  return Object.fromEntries(keys.flatMap(key =>
    typeof source[key] === 'number' && Number.isFinite(source[key])
      ? [[key, source[key] as number]]
      : [],
  )) as Partial<Record<K, number>>;
}

export function summarizePreviewAudioStats(report: StatsReport) {
  const rows: Stat[] = [];
  report.forEach(row => rows.push(row));
  const byId = new Map(rows.map(row => [row.id, row]));
  const transport = rows.find(row =>
    row.type === 'transport' && typeof row.selectedCandidatePairId === 'string',
  );
  const pair = transport ? byId.get(transport.selectedCandidatePairId) : undefined;
  const local = pair ? byId.get(pair.localCandidateId) : undefined;
  const candidateType = ['host', 'srflx', 'prflx', 'relay'].includes(String(local?.candidateType))
    ? local?.candidateType : undefined;
  const protocol = ['udp', 'tcp'].includes(String(local?.protocol)) ? local?.protocol : undefined;
  const relayProtocol = ['udp', 'tcp', 'tls'].includes(String(local?.relayProtocol))
    ? local?.relayProtocol : undefined;
  const media = rows.filter(row =>
    ['inbound-rtp', 'outbound-rtp', 'remote-inbound-rtp', 'media-source'].includes(String(row.type))
    && (row.kind === 'audio' || row.mediaType === 'audio'),
  ).map(row => {
    const codec = byId.get(row.codecId);
    return {
      type: row.type,
      timestamp: typeof row.timestamp === 'number' ? row.timestamp : undefined,
      codec: typeof codec?.mimeType === 'string' && /^audio\/[\w.+-]+$/i.test(codec.mimeType)
        ? codec.mimeType : undefined,
      clockRate: codec ? pickNumbers(codec, ['clockRate']).clockRate : undefined,
      ...pickNumbers(row, mediaFields),
    };
  });
  return {
    connection: pair ? {
      candidateType, protocol, relayProtocol,
      ...pickNumbers(pair, ['currentRoundTripTime', 'availableOutgoingBitrate', 'bytesSent', 'bytesReceived']),
    } : undefined,
    media,
  };
}

export function startPreviewAudioDiagnostics(
  pc: RTCPeerConnection,
  conversationId: string,
  isCurrent: () => boolean,
  emit: (line: string) => void = line => console.info('[PreviewAudio]', line),
  intervalMs = 5000,
): () => void {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const stop = () => { stopped = true; clearTimeout(timer); };
  const current = () => !stopped && isCurrent() && pc.connectionState !== 'closed';
  const sample = async () => {
    if (!current()) { stop(); return; }
    try {
      const report = await pc.getStats();
      if (!current()) { stop(); return; }
      const microphones = pc.getSenders().flatMap(sender => {
        if (!sender.track || sender.track.kind !== 'audio') return [];
        const settings = sender.track.getSettings();
        return [{
          enabled: sender.track.enabled,
          muted: sender.track.muted,
          readyState: sender.track.readyState,
          sampleRate: settings.sampleRate,
          channelCount: settings.channelCount,
          echoCancellation: settings.echoCancellation,
          noiseSuppression: settings.noiseSuppression,
          autoGainControl: settings.autoGainControl,
        }];
      });
      emit(JSON.stringify({
        conversationId, at: new Date().toISOString(),
        connectionState: pc.connectionState, iceConnectionState: pc.iceConnectionState,
        microphones, ...summarizePreviewAudioStats(report),
      }));
    } catch {
      // Diagnostics must never alter call state or expose native exception data.
    } finally {
      if (current()) timer = setTimeout(() => { void sample(); }, intervalMs);
    }
  };
  void sample();
  return stop;
}
